import { parsePhoneNumberFromString } from 'libphonenumber-js';

export type TQueryKind = 'empty' | 'digits' | 'name' | 'none';

export interface IMatchRange {
  start: number;
  end: number;
}

export interface IMatchable {
  name: string;
  phone: string;
}

const LETTER = /\p{L}/u;

export const digitsOf = (value: string): string => value.replace(/\D+/g, '');

const nationalDigitsOf = (phone: string): string => {
  const digits = digitsOf(phone);
  const trimmed = phone.trim();
  if (trimmed.startsWith('+')) {
    const national = parsePhoneNumberFromString(trimmed)?.nationalNumber;
    if (national && digits.endsWith(national)) return national;
  }
  if (digits.length === 11 && digits.startsWith('1')) return digits.slice(1);
  return digits;
};

export const digitMatchOffset = (phone: string, query: string): number => {
  const wanted = digitsOf(query);
  const digits = digitsOf(phone);
  if (!wanted || !digits) return -1;
  const national = nationalDigitsOf(phone);
  if (national.startsWith(wanted)) return digits.length - national.length;
  return digits.startsWith(wanted) ? 0 : -1;
};

export const queryKind = (query: string): TQueryKind => {
  const trimmed = query.trim();
  if (trimmed.length === 0) return 'empty';
  if (LETTER.test(trimmed)) return 'name';
  if (/\d/.test(trimmed)) return 'digits';
  return 'none';
};

const foldChar = (ch: string): string => ch.normalize('NFD').replace(/\p{M}/gu, '').toLowerCase();

const fold = (value: string): string => [...value].map(foldChar).join('');

const words = (query: string): string[] => fold(query.trim()).split(/\s+/).filter(Boolean);

export const personMatches = (person: IMatchable, query: string): boolean => {
  const kind = queryKind(query);
  if (kind === 'empty') return true;
  if (kind === 'none') return false;
  if (kind === 'digits') return digitMatchOffset(person.phone, query) >= 0;
  const name = fold(person.name);
  return words(query).every(word => name.includes(word));
};

const foldWithMap = (value: string): { folded: string; starts: number[]; ends: number[] } => {
  let folded = '';
  const starts: number[] = [];
  const ends: number[] = [];
  let index = 0;
  [...value].forEach(ch => {
    const f = foldChar(ch);
    for (let k = 0; k < f.length; k += 1) {
      starts.push(index);
      ends.push(index + ch.length);
    }
    folded += f;
    index += ch.length;
  });
  return { folded, starts, ends };
};

export const nameMatchRanges = (name: string, query: string): IMatchRange[] => {
  if (queryKind(query) !== 'name') return [];
  const { folded, starts, ends } = foldWithMap(name);
  return words(query)
    .map(word => {
      const at = folded.indexOf(word);
      return at < 0 ? null : { start: starts[at], end: ends[at + word.length - 1] };
    })
    .filter((r): r is IMatchRange => r !== null)
    .sort((a, b) => a.start - b.start);
};

export const digitMatchRange = (display: string, query: string): IMatchRange | null => {
  const wanted = digitsOf(query);
  if (!wanted) return null;
  const positions: number[] = [];
  [...display].forEach((ch, i) => {
    if (/\d/.test(ch)) positions.push(i);
  });
  const at = digitMatchOffset(display, query);
  if (at < 0) return null;
  const first = positions[at];
  const start = first > 0 && display[first - 1] === '(' ? first - 1 : first;
  return { start, end: positions[at + wanted.length - 1] + 1 };
};
